import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { GoodsReceiptDrawer } from "../components/inventory/GoodsReceiptDrawer";
import { BulkStockImportDrawer } from "../components/inventory/BulkStockImportDrawer";
import { MovementTimelineDrawer } from "../components/inventory/MovementTimelineDrawer";
import { QuickCountSection } from "../components/inventory/QuickCountSection";
import { DailyMovementsSection } from "../components/inventory/DailyMovementsSection";
import { ManualReceiptsSection } from "../components/inventory/ManualReceiptsSection";
import { ConversionDrawer, BindOrderDrawer, type ConvertSource, type BindBatch } from "../components/inventory/StockOpsDrawers";
import { StockOverviewSection, type OverviewItem } from "../components/inventory/StockOverviewSection";
import { StockAdjustmentDrawer, PurchaseRequestDrawer } from "../components/inventory/StockDashboardDrawers";
import { ConsumptionTrendDrawer } from "../components/inventory/ConsumptionTrendDrawer";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../components/ui/Toast";
import { Icon } from "../components/ui/Icon";
import { SearchSelect, type SSOption, type SSGroup } from "../components/ui/SearchSelect";
import { TraceViewerDrawer } from "../components/qc/TraceViewerDrawer";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard, Chips, Sheet } from "../components/mobile/primitives";
import { useDebounced } from "../lib/useDebounced";
import "./Items.css";
import "./Inventory.css";

interface LotRow {
  id: string;
  code: string;
  itemKind: "stock" | "semi" | "finished";
  itemId: string;
  itemCode: string;
  itemName: string;
  qtyOnHand: number;
  uom: string;
  producedAt: string;
  expiresAt: string | null;
  status: "pending_qc" | "released" | "on_hold" | "expired" | "rejected" | "consumed";
  supplierLot: string | null;
  supplierName: string | null;
  batchId: string | null;
  batchOrderId: string | null;
  batchOrderCode: string | null;
  /** Lotun kaynağı: üretim · satın alma · sayım/açılış stoğu. */
  origin?: "production" | "purchase" | "opening_stock";
  isConversion: boolean;
  /**
   * İlişkili siparişler — "Sipariş No / Müşteri" süzgeciyle AYNI tanım (uretim: parti/iş emri bağı, sevk: sevkiyat satırı).
   * `eslesti`: süzgeç varken aramaya uyan sipariş — sunucu bunları BAŞA dizer.
   */
  siparisler: Array<{ code: string; musteri: string | null; iliski: "uretim" | "sevk"; eslesti: boolean }>;
}

/** Masaüstü satırında ilk kaç sipariş yazılır; fazlası "+N" düğmesiyle açılır. */
const LOT_SIP_SINIR = 2;

/**
 * Satırın ilişkili sipariş etiketi: süzgeç NEDEN eşleşti, okuyan görsün. Aramaya uyan sipariş vurgulanır ve
 * (sunucu sırasıyla) başta gelir — "+N" içinde kaybolmaz. Masaüstünde ilk iki sipariş yazılır, kalanı "+N"
 * düğmesiyle açılır (title yalnız fareye yeter, dokunmatik/klavye için düğme); mobil kartta `hepsi` ile tümü.
 * Sevk bağı "Sevk:" önekiyle üretim bağından ayrılır.
 */
function LotSiparisleri({ siparisler, hepsi = false }: { siparisler: LotRow["siparisler"]; hepsi?: boolean }) {
  const [acik, setAcik] = useState(false);
  if (!siparisler.length) return null;
  const katlanir = !hepsi && siparisler.length > LOT_SIP_SINIR;
  const goster = katlanir && !acik ? siparisler.slice(0, LOT_SIP_SINIR) : siparisler;
  const tumu = siparisler.map((x) => `${x.iliski === "sevk" ? "Sevk: " : ""}${x.code}${x.musteri ? ` · ${x.musteri}` : ""}`).join("\n");
  return (
    <span className="lot-sip" title={tumu}>
      {goster.map((x, i) => (
        <span key={x.code} className={`lot-sip-oge${x.eslesti ? " eslesti" : ""}`}>
          {i === 0 ? <Icon name="fileText" size={11} /> : null}
          {x.iliski === "sevk" ? <span className="lot-sip-sevk">Sevk:</span> : null}
          <span className="mono">{x.code}</span>
          {x.musteri ? <span className="lot-sip-mus">{x.musteri}</span> : null}
        </span>
      ))}
      {katlanir ? (
        <button type="button" className="lot-sip-fazla" aria-expanded={acik} onClick={() => setAcik((v) => !v)}
          aria-label={acik ? "Sipariş listesini daralt" : `${siparisler.length - LOT_SIP_SINIR} sipariş daha göster`}>
          {acik ? "Daralt" : `+${siparisler.length - LOT_SIP_SINIR}`}
        </button>
      ) : null}
    </span>
  );
}

const STATUS_BADGE: Record<LotRow["status"], { cls: string; label: string }> = {
  released: { cls: "b-success", label: "Serbest" },
  pending_qc: { cls: "b-warn", label: "Kalite Kontrol Bekleniyor" },
  on_hold: { cls: "b-warn", label: "Blokeli" },
  expired: { cls: "", label: "SKT Geçti" },
  rejected: { cls: "", label: "Reddedildi" },
  consumed: { cls: "", label: "Tükendi" },
};

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}
function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}

// SKT'ye kalan gün (uyarı rengi için)
function expiryWarn(expiresAt: string | null): "ok" | "soon" | "past" {
  if (!expiresAt) return "ok";
  const days = (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (days < 0) return "past";
  if (days < 30) return "soon";
  return "ok";
}

type Kind = "stock" | "semi" | "finished";
const KIND_LABELS: [Kind, string][] = [["stock", "Stok"], ["semi", "Yarı Mamül"], ["finished", "Mamul"]];
const KIND_LABEL_MAP: Record<string, string> = { stock: "Stok", semi: "Yarı Mamül", finished: "Mamul" };
const fLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--neutral-500)", textTransform: "uppercase", letterSpacing: "0.04em" };

const invTabStyle = (active: boolean): React.CSSProperties => ({
  padding: "8px 14px", fontSize: 13, fontWeight: active ? 600 : 500,
  color: active ? "var(--primary)" : "var(--neutral-600)",
  background: "transparent", border: 0,
  borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
  cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap",
});

export function InventoryPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lotFilterOpen, setLotFilterOpen] = useState(false); // mobil Lotlar filtre sheet'i
  const [tab, setTab] = useState<"overview" | "receipts" | "count" | "lots" | "daily">("overview");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [onlyInStock, setOnlyInStock] = useState(true);
  const [kinds, setKinds] = useState<Record<Kind, boolean>>({ stock: true, semi: true, finished: true });
  const [supplierId, setSupplierId] = useState("");
  const [itemId, setItemId] = useState("");
  const [itemTypeId, setItemTypeId] = useState("");
  // Sipariş no / müşteri süzgeci — yazarken her tuşta istek gitmesin (yalnız lot sorgusu tüketir).
  const [siparisQ, setSiparisQ] = useState("");
  const [musteriQ, setMusteriQ] = useState("");
  const siparisQGecikmeli = useDebounced(siparisQ.trim());
  const musteriQGecikmeli = useDebounced(musteriQ.trim());
  const pageSize = 20;

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptPrefill, setReceiptPrefill] = useState<{ kind: Kind; itemId: string } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Genel Bakış aksiyonları
  const [adjustItem, setAdjustItem] = useState<OverviewItem | null>(null);
  const [poItem, setPoItem] = useState<OverviewItem | null>(null);
  const [trendItem, setTrendItem] = useState<OverviewItem | null>(null);
  const [timeline, setTimeline] = useState<{ id: string; code: string } | null>(null);
  const [trace, setTrace] = useState<{ id: string; code: string } | null>(null);
  // Barkod/QR tarayıcıdan gelen ?trace=<lotId> → o lotun İzlenebilirlik drawer'ını aç (param temizlenir).
  useEffect(() => {
    const t = searchParams.get("trace");
    if (t) {
      setTrace({ id: t, code: "" });
      const sp = new URLSearchParams(searchParams);
      sp.delete("trace");
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Stok işlemleri: dönüştür / siparişe bağla / stoğa al (kopar)
  const [convertSrc, setConvertSrc] = useState<ConvertSource | null>(null);
  const [bindBatch, setBindBatch] = useState<BindBatch | null>(null);
  const [detachBatch, setDetachBatch] = useState<{ batchId: string; code: string; orderCode: string | null } | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const pushToast = useCallback((t: Omit<ToastItem, "id">) => setToasts((s) => [...s, { ...t, id: `${Date.now()}-${Math.random()}` }]), []);
  const detachMut = trpc.productionBatch.detachFromOrder.useMutation({
    onSuccess: (res) => { qc.invalidateQueries(); pushToast({ tone: "success", message: `${res.code} stoğa alındı${res.reopened ? " · sipariş yeniden üretimde" : ""}.` }); setDetachBatch(null); },
    onError: (err) => pushToast({ tone: "danger", title: "Stoğa alınamadı", message: err.message }),
  });

  const input = useMemo(() => {
    const ks = (Object.keys(kinds) as Kind[]).filter((k) => kinds[k]);
    return {
      page, pageSize,
      q: q.trim() || undefined,
      onlyInStock,
      kinds: ks,
      supplierId: supplierId || undefined,
      itemId: itemId || undefined,
      itemTypeId: itemTypeId || undefined,
      siparisQ: siparisQGecikmeli || undefined,
      musteriQ: musteriQGecikmeli || undefined,
    };
  }, [page, q, onlyInStock, kinds, supplierId, itemId, itemTypeId, siparisQGecikmeli, musteriQGecikmeli]);
  const lotsQ = trpc.inventory.lots.useQuery(input);
  const expiryMut = trpc.inventory.runExpiry.useMutation({ onSuccess: () => qc.invalidateQueries() });

  /**
   * ENVANTER RAPORU — /print/stok-raporu YENİ SEKMEDE (kardeş baskı sayfalarıyla aynı; masaüstü + mobil ortak).
   * Ekran önizleme + A4 + Excel orada, AYNI uçtan ve AYNI süzgeçle. Varsayılan (URL'siz): Mamul + Ürün bazında +
   * Yalnız stokta — müşterinin ana ihtiyacı (mamul stoğunda elde olanı ayırmak); süzgeçler raporda değiştirilir.
   * ⚠ `window.open(url, "_blank", "noopener")` SPEC GEREĞİ HER ZAMAN null döner → "engellendi" uyarısı rapor
   * açılsa bile basılırdı. noopener, açılan pencerenin `opener` bağı koparılarak sağlanır; null GERÇEKTEN engeldir.
   */
  const stokRaporuAc = useCallback(() => {
    const w = window.open("/print/stok-raporu", "_blank");
    if (w) w.opener = null;
    else pushToast({ tone: "warning", title: "Stok raporu açılamadı", message: "Tarayıcı açılır pencereyi engelledi. Bu site için izin verip tekrar deneyin." });
  }, [pushToast]);

  // Filtre dropdown verileri
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { staleTime: 60_000 });
  const itemOptsQ = trpc.inventory.itemOptions.useQuery(undefined, { staleTime: 60_000 });
  const itemTypesQ = trpc.settings.itemType.list.useQuery({ includeInactive: false }, { staleTime: 60_000 });
  const suppliers = suppliersQ.data?.rows ?? [];
  const itemOpts = (itemOptsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>;
  const itemTypes = (itemTypesQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>;
  const siparisSuzgeci = !!siparisQ.trim() || !!musteriQ.trim();
  const filtersActive = !!supplierId || !!itemId || !!itemTypeId || siparisSuzgeci || KIND_LABELS.some(([k]) => !kinds[k]);

  // SearchSelect seçenekleri
  const supplierOptions = useMemo<SSOption[]>(() => suppliers.map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliers]);
  const itemOptions = useMemo<SSGroup[]>(() => {
    const byKind: Record<string, SSOption[]> = { stock: [], semi: [], finished: [] };
    itemOpts.forEach((i) => byKind[i.kind]?.push({ value: i.id, label: i.name, tail: i.code }));
    const groups: SSGroup[] = [];
    for (const [k, lbl] of KIND_LABELS) if (byKind[k] && byKind[k]!.length) groups.push({ group: lbl, items: byKind[k]! });
    return groups;
  }, [itemOpts]);
  const typeOptions = useMemo<SSOption[]>(() => itemTypes.map((t) => ({ value: t.id, label: t.name, desc: KIND_LABEL_MAP[t.kind] ?? t.kind })), [itemTypes]);

  function clearFilters() {
    setSupplierId(""); setItemId(""); setItemTypeId(""); setSiparisQ(""); setMusteriQ("");
    setKinds({ stock: true, semi: true, finished: true });
    setPage(1);
  }

  const rows = (lotsQ.data?.rows ?? []) as unknown as LotRow[];
  /** Sorguya GERÇEKTEN giden sipariş/müşteri süzgeci (gecikmeli) — boş liste metni yazılan değil, uygulanan değeri anlatır. */
  const sipSuzgecUygulanan = !!siparisQGecikmeli || !!musteriQGecikmeli;
  /** Boş liste nedeni — sipariş/müşteri süzgeci varsa bunu söyle (genel "lot yok" yanıltır). */
  const bosMetin = sipSuzgecUygulanan
    ? `${[siparisQGecikmeli && `"${siparisQGecikmeli}" siparişi`, musteriQGecikmeli && `"${musteriQGecikmeli}" müşterisi`].filter(Boolean).join(" ve ")} ile ilişkili lot yok.`
      + (onlyInStock ? " Stoğu biten lotlar gizli — “Sadece stokta” kapatılırsa onlar da aranır." : "")
    : q ? `"${q}" için sonuç yok.` : null;
  const total = lotsQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ═══ Aksiyon drawer/dialog'ları — masaüstü + mobil ORTAK (Drawer ≤767px tam-ekran) ═══
  const drawers = (
    <>
      <GoodsReceiptDrawer open={receiptOpen} prefill={receiptPrefill} onClose={() => { setReceiptOpen(false); setReceiptPrefill(null); }} />
      <StockAdjustmentDrawer open={!!adjustItem} item={adjustItem} onClose={() => setAdjustItem(null)} onDone={(msg) => pushToast({ tone: "success", message: msg })} />
      <PurchaseRequestDrawer open={!!poItem} item={poItem} onClose={() => setPoItem(null)} onDone={(msg) => pushToast({ tone: "success", message: msg })} />
      <ConsumptionTrendDrawer open={!!trendItem} item={trendItem} onClose={() => setTrendItem(null)} />
      <BulkStockImportDrawer open={bulkOpen} onClose={() => setBulkOpen(false)} />
      <MovementTimelineDrawer open={!!timeline} lotId={timeline?.id ?? null} lotCode={timeline?.code} onClose={() => setTimeline(null)} />
      <TraceViewerDrawer open={!!trace} lotId={trace?.id ?? null} lotCode={trace?.code} onClose={() => setTrace(null)} />
      <ConversionDrawer open={!!convertSrc} source={convertSrc} onClose={() => setConvertSrc(null)} onDone={(msg) => pushToast({ tone: "success", message: msg })} />
      <BindOrderDrawer open={!!bindBatch} batch={bindBatch} onClose={() => setBindBatch(null)} onDone={(msg) => pushToast({ tone: "success", message: msg })} />
      <ConfirmDialog
        open={!!detachBatch}
        title="Siparişten kopar (stoğa al)"
        message={<>Bu parti (<b className="mono">{detachBatch?.code}</b>){detachBatch?.orderCode ? <> <b className="mono">{detachBatch.orderCode}</b> siparişinden</> : " siparişten"} koparılıp serbest stoğa alınacak. İzlenebilirlik değişmez; sipariş rezervasyonu bırakılır. Devam edilsin mi?</>}
        confirmLabel="Stoğa Al"
        tone="primary"
        busy={detachMut.isPending}
        onConfirm={() => detachBatch && detachMut.mutate({ batchId: detachBatch.batchId })}
        onCancel={() => setDetachBatch(null)}
      />
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((s) => s.filter((t) => t.id !== id))} />
    </>
  );

  // Lot kartı aksiyonları (masaüstü tablo satırıyla birebir koşullar)
  const lotActions = (l: LotRow) => (
    <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
      {l.status === "released" && l.qtyOnHand > 0 ? (
        <button type="button" className="m-iconbtn m-li-act" aria-label="Dönüştür" onClick={() => setConvertSrc({ id: l.id, code: l.code, itemKind: l.itemKind, itemId: l.itemId, itemCode: l.itemCode, itemName: l.itemName, qtyOnHand: l.qtyOnHand, uom: l.uom })}><Icon name="shuffle" size={16} /></button>
      ) : null}
      {l.batchId && !l.batchOrderId && l.status === "released" && l.qtyOnHand > 0 && (l.itemKind === "semi" || l.itemKind === "finished") ? (
        <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Siparişe bağla" onClick={() => setBindBatch({ batchId: l.batchId!, itemKind: l.itemKind as "semi" | "finished", itemId: l.itemId, itemCode: l.itemCode, itemName: l.itemName, lotCode: l.code, qtyOnHand: l.qtyOnHand, uom: l.uom })}><Icon name="arrowRight" size={16} /></button>
      ) : null}
      {l.batchId && l.batchOrderId ? (
        <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--warning)" }} aria-label="Stoğa al (siparişten kopar)" onClick={() => setDetachBatch({ batchId: l.batchId!, code: l.code, orderCode: l.batchOrderCode })}><Icon name="package" size={16} /></button>
      ) : null}
      <button type="button" className="m-iconbtn m-li-act" aria-label="Geçmiş" onClick={() => setTimeline({ id: l.id, code: l.code })}><Icon name="clock" size={16} /></button>
      <button type="button" className="m-iconbtn m-li-act" aria-label="İzle" onClick={() => setTrace({ id: l.id, code: l.code })}><Icon name="search" size={16} /></button>
    </div>
  );

  // ═══ MOBİL GÖRÜNÜM (≤767px) — sekme chips + kart listeleri ═══
  if (isMobile) {
    const lotFilterCount = (supplierId ? 1 : 0) + (itemId ? 1 : 0) + (itemTypeId ? 1 : 0) + (siparisQ.trim() ? 1 : 0) + (musteriQ.trim() ? 1 : 0) + KIND_LABELS.filter(([k]) => !kinds[k]).length;
    return (
      <>
        <div className="m-stickytop">
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn-md primary sm" style={{ flex: 1 }} onClick={() => setReceiptOpen(true)}><Icon name="plus" size={14} />Mal Kabul</button>
            {tab !== "daily" && tab !== "count" ? (
              <button type="button" className="btn-md secondary sm" onClick={() => setBulkOpen(true)}><Icon name="upload" size={14} />Toplu</button>
            ) : null}
          </div>
          <Chips>
            <button type="button" className={`m-chip ${tab === "overview" ? "act" : ""}`} onClick={() => setTab("overview")}>Genel Bakış</button>
            <button type="button" className={`m-chip ${tab === "receipts" ? "act" : ""}`} onClick={() => setTab("receipts")}>Mal Kabul</button>
            <button type="button" className={`m-chip ${tab === "count" ? "act" : ""}`} onClick={() => setTab("count")}>Hızlı Sayım</button>
            <button type="button" className={`m-chip ${tab === "lots" ? "act" : ""}`} onClick={() => setTab("lots")}>Lotlar</button>
            <button type="button" className={`m-chip ${tab === "daily" ? "act" : ""}`} onClick={() => setTab("daily")}>Günlük Hareketler</button>
          </Chips>
        </div>

        {/* ENVANTER RAPORU — mobil giriş (masaüstündeki "Stok Raporu" ile aynı sayfa). Kart akışının doğrudan
            çocuğu: .m-content sütununda tam genişlik, .m-app .btn-md → 44px dokunma hedefi. */}
        {tab === "overview" || tab === "lots" ? (
          <button type="button" className="btn-md secondary" onClick={stokRaporuAc}>
            <Icon name="printer" size={15} />Stok Raporu (ürün / lot bazında)
          </button>
        ) : null}

        {tab === "overview" ? (
          <StockOverviewSection isMobile
            onReceipt={(it) => { setReceiptPrefill({ kind: it.kind, itemId: it.id }); setReceiptOpen(true); }}
            onAdjust={(it) => setAdjustItem(it)}
            onPoRequest={(it) => setPoItem(it)}
            onTrend={(it) => setTrendItem(it)}
          />
        ) : tab === "receipts" ? (
          <ManualReceiptsSection />
        ) : tab === "count" ? (
          <QuickCountSection />
        ) : tab === "daily" ? (
          <DailyMovementsSection isMobile />
        ) : (
          <>
            {expiryMut.data ? (
              <div style={{ padding: "8px 12px", background: "var(--info-subtle)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--info-text)" }}>
                SKT taraması: {expiryMut.data.expiredCount} lot "SKT Geçti" olarak işaretlendi.
              </div>
            ) : null}
            <div className="m-search" style={{ height: 44 }}>
              <Icon name="search" size={15} />
              <input type="search" placeholder="Lot kodu, tedarikçi parti ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button type="button" className={`m-chip ${onlyInStock ? "act" : ""}`} onClick={() => { setOnlyInStock(!onlyInStock); setPage(1); }}>Sadece stokta</button>
              <span style={{ flex: 1 }} />
              <button type="button" className="btn-md secondary sm" onClick={() => expiryMut.mutate()} disabled={expiryMut.isPending}><Icon name="clock" size={14} />{expiryMut.isPending ? "…" : "SKT Tara"}</button>
              <button type="button" className={`btn-md sm ${lotFilterCount ? "primary" : "secondary"}`} onClick={() => setLotFilterOpen(true)}><Icon name="list2" size={14} />{lotFilterCount ? `(${lotFilterCount})` : "Filtre"}</button>
            </div>
            {lotsQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
            : lotsQ.isError ? <div className="m-empty" style={{ color: "var(--danger)" }}>{lotsQ.error?.message}</div>
            : rows.length === 0 ? <div className="m-empty">{bosMetin ?? "Lot yok — ilk mal kabulü yapın."}</div>
            : rows.map((l) => {
              const sb = STATUS_BADGE[l.status];
              const warn = expiryWarn(l.expiresAt);
              return (
                <MCard pad key={l.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{l.code}</span>
                    <span className={`badge ${sb.cls === "b-warn" ? "b-warning" : sb.cls || "b-neutral"} no-dot`}>{sb.label}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginTop: 5 }}>
                    {l.itemName}
                    {l.isConversion ? <span className="badge b-info no-dot" style={{ marginLeft: 6, fontSize: 10 }}>Dönüşüm</span> : null}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--neutral-400)", marginTop: 1 }}>{l.itemCode}</div>
                  <LotSiparisleri siparisler={l.siparisler ?? []} hepsi />
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1 }}><div className="m-ovl">Bakiye</div><div className="mono" style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2 }}>{fmtQty(l.qtyOnHand)} {l.uom}</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">SKT</div><div className="mono" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2, color: warn === "past" ? "var(--danger)" : warn === "soon" ? "var(--warning)" : "var(--neutral-700)" }}>{fmtDate(l.expiresAt)}</div></div>
                    <div style={{ flex: 1.2 }}><div className="m-ovl">Tedarikçi</div><div style={{ fontSize: 12, fontWeight: 500, marginTop: 2, color: l.supplierLot ? "var(--neutral-800)" : "var(--neutral-400)" }}>{l.supplierLot ? <span className="mono">{l.supplierLot}</span> : "—"}</div></div>
                  </div>
                  {lotActions(l)}
                </MCard>
              );
            })}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}

        <Sheet open={lotFilterOpen} title="Lot Filtresi" onClose={() => setLotFilterOpen(false)}
          footer={<>
            <button type="button" className="btn-md secondary" onClick={clearFilters}>Temizle</button>
            <button type="button" className="btn-md primary" onClick={() => setLotFilterOpen(false)}>Uygula</button>
          </>}>
          <div className="field lot-mfld">
            <label className="field-label" htmlFor="lot-m-sip">Sipariş No</label>
            <input id="lot-m-sip" className="form-input" type="search" inputMode="text" autoComplete="off" enterKeyHint="search" maxLength={60}
              value={siparisQ} onChange={(e) => { setSiparisQ(e.target.value); setPage(1); }} placeholder="Örn. SO-2026-00154" />
          </div>
          <div className="field lot-mfld">
            <label className="field-label" htmlFor="lot-m-mus">Müşteri</label>
            <input id="lot-m-mus" className="form-input" type="search" autoComplete="off" enterKeyHint="search" maxLength={120}
              value={musteriQ} onChange={(e) => { setMusteriQ(e.target.value); setPage(1); }} placeholder="Müşteri adı ya da kodu" />
          </div>
          <div className="field lot-mfld">
            <label className="field-label">Tedarikçi</label>
            <SearchSelect value={supplierId || null} options={supplierOptions} onChange={(v) => { setSupplierId(v ?? ""); setPage(1); }} placeholder="Tüm tedarikçiler" searchPlaceholder="Tedarikçi ara…" />
          </div>
          <div className="field lot-mfld">
            <label className="field-label">Ürün</label>
            <SearchSelect value={itemId || null} options={itemOptions} onChange={(v) => { setItemId(v ?? ""); setPage(1); }} placeholder="Tüm ürünler" searchPlaceholder="Ürün adı / kodu ara…" />
          </div>
          <div className="field lot-mfld">
            <label className="field-label">Kart Türü</label>
            <SearchSelect value={itemTypeId || null} options={typeOptions} onChange={(v) => { setItemTypeId(v ?? ""); setPage(1); }} placeholder="Tüm türler" searchPlaceholder="Tür ara…" />
          </div>
          <div className="field">
            <label className="field-label">Tür</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {KIND_LABELS.map(([k, lbl]) => (
                <button type="button" key={k} className={`m-chip ${kinds[k] ? "act" : ""}`} onClick={() => { setKinds((p) => ({ ...p, [k]: !p[k] })); setPage(1); }}>{lbl}</button>
              ))}
            </div>
          </div>
        </Sheet>

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Stok / Lotlar</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Stok / Lotlar</h1>
          <p className="sub">Lot bazlı envanter · FEFO · SKT takibi · günlük hareketler</p>
        </div>
        {tab !== "daily" && tab !== "count" ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {tab === "lots" ? (
              <button type="button" className="btn-md secondary" onClick={() => expiryMut.mutate()} disabled={expiryMut.isPending} title="SKT geçen lotları işaretle">
                <Icon name="clock" size={14} />{expiryMut.isPending ? "Taranıyor…" : "SKT Tara"}
              </button>
            ) : null}
            {/* ENVANTER RAPORU — ürün / lot bazında; ekran önizleme + A4 + Excel yeni sekmede (sayfasız tam döküm). */}
            <button type="button" className="btn-md secondary" onClick={stokRaporuAc}
              title="Envanter raporu — ürün bazında ya da lot bazında; ekranda önizle, yazdır, Excel indir (yeni sekme)">
              <Icon name="printer" size={14} />Stok Raporu
            </button>
            <button type="button" className="btn-md secondary" onClick={() => setBulkOpen(true)} title="Excel ile toplu stok/parti girişi">
              <Icon name="upload" size={14} />Toplu Stok/Parti
            </button>
            <button type="button" className="btn-md primary" onClick={() => setReceiptOpen(true)}><Icon name="plus" size={14} />Mal Kabul</button>
          </div>
        ) : null}
      </div>

      {/* Sekmeler */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid var(--neutral-200)" }}>
        <button type="button" style={invTabStyle(tab === "overview")} onClick={() => setTab("overview")}>Genel Bakış</button>
        <button type="button" style={invTabStyle(tab === "receipts")} onClick={() => setTab("receipts")}>Mal Kabul</button>
        <button type="button" style={invTabStyle(tab === "count")} onClick={() => setTab("count")}>Hızlı Sayım</button>
        <button type="button" style={invTabStyle(tab === "lots")} onClick={() => setTab("lots")}>Lotlar</button>
        <button type="button" style={invTabStyle(tab === "daily")} onClick={() => setTab("daily")}>Günlük Hareketler</button>
      </div>

      {tab === "overview" ? (
        <StockOverviewSection
          onReceipt={(it) => { setReceiptPrefill({ kind: it.kind, itemId: it.id }); setReceiptOpen(true); }}
          onAdjust={(it) => setAdjustItem(it)}
          onPoRequest={(it) => setPoItem(it)}
          onTrend={(it) => setTrendItem(it)}
        />
      ) : null}

      {tab === "receipts" ? <ManualReceiptsSection /> : null}

      {tab === "count" ? <QuickCountSection /> : null}

      {tab === "daily" ? <DailyMovementsSection /> : null}

      {tab === "lots" ? (
      <>
      {expiryMut.data ? (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--info-subtle)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--info-text)" }}>
          SKT taraması: {expiryMut.data.expiredCount} lot "SKT Geçti" olarak işaretlendi.
        </div>
      ) : null}

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Lot kodu, tedarikçi parti ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <label className="form-checkbox" style={{ fontSize: 12.5 }}>
            <input type="checkbox" checked={onlyInStock} onChange={(e) => { setOnlyInStock(e.target.checked); setPage(1); }} />
            <span>Sadece stokta olanlar</span>
          </label>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => lotsQ.refetch()} disabled={lotsQ.isFetching}>
            {lotsQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        {/* Filtre çubuğu */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 18, padding: "11px 14px", borderBottom: "1px solid var(--neutral-200)", background: "var(--white)", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, width: 210 }}>
            <span style={fLabel}>Tedarikçi</span>
            <SearchSelect
              value={supplierId || null}
              options={supplierOptions}
              onChange={(v) => { setSupplierId(v ?? ""); setPage(1); }}
              placeholder="Tüm tedarikçiler"
              searchPlaceholder="Tedarikçi ara…"
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, width: 250 }}>
            <span style={fLabel}>Ürün</span>
            <SearchSelect
              value={itemId || null}
              options={itemOptions}
              onChange={(v) => { setItemId(v ?? ""); setPage(1); }}
              placeholder="Tüm ürünler"
              searchPlaceholder="Ürün adı / kodu ara…"
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, width: 190 }}>
            <span style={fLabel}>Kart Türü</span>
            <SearchSelect
              value={itemTypeId || null}
              options={typeOptions}
              onChange={(v) => { setItemTypeId(v ?? ""); setPage(1); }}
              placeholder="Tüm türler"
              searchPlaceholder="Tür ara…"
            />
          </div>
          {/* Sipariş / müşteri — lotu üreten parti, iş emri ya da sevkiyat bağıyla (sunucu: lotSiparisKosulu).
              İki alan tek grup: dar ekranda birlikte alt satıra iner (biri yukarıda biri aşağıda kalmaz). */}
          <div className="inv-filt-grup">
            <div className="inv-filt">
              <label className="inv-filt-lbl" htmlFor="lot-sip">Sipariş No</label>
              <input id="lot-sip" className="form-input inv-filt-input" type="search" autoComplete="off" maxLength={60}
                value={siparisQ} onChange={(e) => { setSiparisQ(e.target.value); setPage(1); }} placeholder="SO-2026-…" />
            </div>
            <div className="inv-filt inv-filt-genis">
              <label className="inv-filt-lbl" htmlFor="lot-mus">Müşteri</label>
              <input id="lot-mus" className="form-input inv-filt-input" type="search" autoComplete="off" maxLength={120}
                value={musteriQ} onChange={(e) => { setMusteriQ(e.target.value); setPage(1); }} placeholder="Müşteri adı / kodu" />
            </div>
          </div>

          <div style={{ width: 1, alignSelf: "stretch", background: "var(--neutral-200)", margin: "3px 0" }} />

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={fLabel}>Tür</span>
            <div style={{ display: "flex", gap: 14, height: 34, alignItems: "center" }}>
              {KIND_LABELS.map(([k, lbl]) => (
                <label key={k} className="form-checkbox" style={{ fontSize: 12.5 }}>
                  <input type="checkbox" checked={kinds[k]} onChange={(e) => { setKinds((p) => ({ ...p, [k]: e.target.checked })); setPage(1); }} />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
          </div>

          {filtersActive ? (
            <button type="button" className="btn-md ghost" style={{ height: 34, alignSelf: "flex-end", marginLeft: "auto", color: "var(--neutral-600)" }} onClick={clearFilters}>
              <Icon name="x" size={14} />Temizle
            </button>
          ) : null}
        </div>

        <div className="dt2-scroll">
          {lotsQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : lotsQ.isError ? (
            <div className="empty-state"><p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p><p className="sub">{lotsQ.error?.message}</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Lot yok</p>
              <p className="sub">{bosMetin ?? "İlk mal kabulü yapın."}</p>
              {!q && !sipSuzgecUygulanan ? <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={() => setReceiptOpen(true)}><Icon name="plus" size={14} />Mal Kabul</button> : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Lot</th>
                  <th>Ürün</th>
                  <th className="num" style={{ width: 120 }}>Bakiye</th>
                  <th style={{ width: 110 }}>SKT</th>
                  <th style={{ width: 175 }}>Durum</th>
                  <th style={{ width: 130 }}>Tedarikçi</th>
                  <th style={{ width: 84 }}>Hareket</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const sb = STATUS_BADGE[l.status];
                  const warn = expiryWarn(l.expiresAt);
                  return (
                    <tr key={l.id}>
                      <td>
                        <span className="cell-id">{l.code}</span>
                        {/* Kaynak rozeti YALNIZ açılış stoğunda: üretim ve satın alma zaten
                            beklenen durumlar, hepsini rozetlemek tabloyu gürültüye boğardı.
                            Açılış stoğu ise istisnadır — reçete tüketmez, üretim raporuna
                            girmez; okuyanın bunu bilmesi gerekir (renk değil METİN taşır). */}
                        {l.origin === "opening_stock" ? (
                          <span className="lot-origin" title="Sayım öncesi mevcut stok — reçete tüketimi yapılmadı, üretim raporuna girmez">
                            Açılış
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {l.itemName}
                        <span className="sec mono" style={{ fontSize: 10.5 }}>{l.itemCode}</span>
                        <LotSiparisleri siparisler={l.siparisler ?? []} />
                      </td>
                      <td className="cell-num">{fmtQty(l.qtyOnHand)}<span className="unit">{l.uom}</span></td>
                      <td>
                        <span className="mono" style={{ fontSize: 11.5, color: warn === "past" ? "var(--danger)" : warn === "soon" ? "var(--warning)" : "var(--neutral-600)" }}>
                          {fmtDate(l.expiresAt)}
                        </span>
                      </td>
                      <td><span className={`badge-pill ${sb.cls}`}>{sb.label}</span></td>
                      <td style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>
                        {l.supplierLot ? <span className="mono">{l.supplierLot}</span> : "—"}
                      </td>
                      <td>
                        <div className="row-acts">
                          {/* Dönüştür: serbest + stokta olan her lot başka ürüne dönüştürülebilir */}
                          {l.status === "released" && l.qtyOnHand > 0 ? (
                            <button type="button" className="iact info" aria-label="Dönüştür" title="Başka ürüne dönüştür" onClick={() => setConvertSrc({ id: l.id, code: l.code, itemKind: l.itemKind, itemId: l.itemId, itemCode: l.itemCode, itemName: l.itemName, qtyOnHand: l.qtyOnHand, uom: l.uom })}>
                              <Icon name="shuffle" size={15} />
                            </button>
                          ) : null}
                          {/* Siparişe Bağla: stok partisi (siparişsiz) + serbest + üretilebilir */}
                          {l.batchId && !l.batchOrderId && l.status === "released" && l.qtyOnHand > 0 && (l.itemKind === "semi" || l.itemKind === "finished") ? (
                            <button type="button" className="iact success" aria-label="Siparişe bağla" title="Bir siparişe bağla" onClick={() => setBindBatch({ batchId: l.batchId!, itemKind: l.itemKind as "semi" | "finished", itemId: l.itemId, itemCode: l.itemCode, itemName: l.itemName, lotCode: l.code, qtyOnHand: l.qtyOnHand, uom: l.uom })}>
                              <Icon name="arrowRight" size={15} />
                            </button>
                          ) : null}
                          {/* Stoğa Al: siparişe bağlı partiyi kopar */}
                          {l.batchId && l.batchOrderId ? (
                            <button type="button" className="iact warning" aria-label="Stoğa al" title={`Siparişten kopar${l.batchOrderCode ? ` (${l.batchOrderCode})` : ""}`} onClick={() => setDetachBatch({ batchId: l.batchId!, code: l.code, orderCode: l.batchOrderCode })}>
                              <Icon name="package" size={15} />
                            </button>
                          ) : null}
                          <button type="button" className="iact info" aria-label="Geçmiş" title="Geçmiş" onClick={() => setTimeline({ id: l.id, code: l.code })}>
                            <Icon name="clock" size={15} />
                          </button>
                          <button type="button" className="iact info" aria-label="İzle" title="İzle" onClick={() => setTrace({ id: l.id, code: l.code })}>
                            <Icon name="search" size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{total}</b> lot · <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>
      </>
      ) : null}

      {drawers}
    </div>
  );
}
